import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { isClickedOutside, isMobileBreakpoint } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Optional checkout click tracing: set `window.__DEBUG_CART_CHECKOUT = true` in the console, or
 * `sessionStorage.setItem('debugCartCheckout','1')` then reload. Remove this block when done.
 */

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {AbortController | null} */
  #checkoutDebugAbort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#checkoutDebugAbort = new AbortController();
    const { signal } = this.#checkoutDebugAbort;
    const fmt = (n) =>
      n instanceof Element
        ? `${n.tagName.toLowerCase()}${n.id ? `#${n.id}` : ''}${
            n.className && typeof n.className === 'string'
              ? `.${String(n.className).trim().replace(/\s+/g, '.')}`
              : ''
          }`
        : n?.nodeName ?? String(n);

    /** @param {PointerEvent | MouseEvent} ev */
    const logCheckoutHit = (ev) => {
      if (
        typeof window === 'undefined' ||
        (window.__DEBUG_CART_CHECKOUT !== true &&
          (typeof sessionStorage === 'undefined' || sessionStorage.getItem('debugCartCheckout') !== '1'))
      ) {
        return;
      }
      const dialog = this.refs.dialog;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
      const checkout = dialog.querySelector('#checkout');
      if (!(checkout instanceof HTMLButtonElement)) return;

      const pathEls =
        typeof ev.composedPath === 'function'
          ? ev.composedPath().filter((n) => n instanceof Element)
          : [];
      const stack = document.elementsFromPoint(ev.clientX, ev.clientY);
      const top = stack[0];
      const involvesCheckout =
        pathEls.some((el) => checkout === el || checkout.contains(el)) ||
        (top instanceof Element && (checkout === top || checkout.contains(top)));

      if (!involvesCheckout) return;

      const form = checkout.form;
      const dr = dialog.getBoundingClientRect();
      console.log('[cart-checkout-debug]', ev.type, {
        defaultPrevented: ev.defaultPrevented,
        eventPhase: ev.eventPhase,
        target: fmt(ev.target),
        targetIsElement: ev.target instanceof Element,
        checkoutDisabled: checkout.disabled,
        formDisabled: form?.disabled ?? null,
        isClickedOutsideDialog: isClickedOutside(ev, dialog),
        dialogRect: { top: dr.top, left: dr.left, right: dr.right, bottom: dr.bottom, width: dr.width, height: dr.height },
        point: { x: ev.clientX, y: ev.clientY },
        composedPathTop10: pathEls.slice(0, 10).map(fmt),
        elementsFromPointTop8: stack.slice(0, 8).map(fmt),
      });
    };

    this.addEventListener('pointerdown', logCheckoutHit, { capture: true, signal });
    this.addEventListener('click', logCheckoutHit, { capture: true, signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#syncEmptyStateFromCart);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('keydown', this.#onDocumentEscape, true);
    this.addEventListener(DialogOpenEvent.eventName, this.#refreshCartSection);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#setOpenAttribute);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#removeOpenAttribute);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    this.#checkoutDebugAbort?.abort();
    this.#checkoutDebugAbort = null;
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#syncEmptyStateFromCart);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('keydown', this.#onDocumentEscape, true);
    this.removeEventListener(DialogOpenEvent.eventName, this.#refreshCartSection);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#setOpenAttribute);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#removeOpenAttribute);
    this.#historyAbortController?.abort();
  }

  /**
   * Refresh cart markup when the drawer opens.
   * Run after paint / when idle: doing a section morph synchronously on open fought iOS Safari
   * (body fixed + showModal + heavy header hydration) and could lock the main thread.
   */
  #refreshCartSection = () => {
    const run = () => {
      try {
        const cartItems = this.querySelector('cart-items-component[data-drawer]');
        const sectionId = cartItems?.dataset.sectionId;
        if (!sectionId) return;
        sectionRenderer.renderSection(sectionId, { cache: false, mode: 'hydration' });
      } catch (e) {
        console.warn('[cart-drawer] section refresh failed', e);
      }
    };

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(run, { timeout: 400 });
    } else {
      setTimeout(run, 50);
    }
  };

  #setOpenAttribute = () => {
    this.setAttribute('open', '');
    const trigger = this.querySelector('[data-testid="cart-drawer-trigger"]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  };

  #removeOpenAttribute = () => {
    this.removeAttribute('open');
    const trigger = this.querySelector('[data-testid="cart-drawer-trigger"]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /** Non-modal dialog: focus can stay on header; still close the bag on Escape. */
  #onDocumentEscape = (event) => {
    if (event.key !== 'Escape') return;
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    event.preventDefault();
    this.closeDialog();
  };

  #handleCartAdd = (event) => {
    if (event?.addedToBagDrawerShown) return;
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Keep dialog empty-state class / aria in sync after cart API + section morph (hydration
   * may not rewrite the dialog node).
   */
  #syncEmptyStateFromCart = (event) => {
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    const raw =
      event.detail?.data?.itemCount ??
      event.detail?.resource?.item_count ??
      event.detail?.resource?.items?.length;
    if (raw === undefined || raw === null) return;

    const isEmpty = Number(raw) === 0;
    dialog.classList.toggle('cart-drawer--empty', isEmpty);
    dialog.setAttribute('aria-labelledby', isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    requestAnimationFrame(() => {
      if (!dialog.isConnected || !dialog.open) return;

      const content = dialog.querySelector('.cart-drawer__content');
      const summary = dialog.querySelector('.cart-drawer__summary');

      if (!content || !summary) {
        dialog.setAttribute('cart-summary-sticky', 'false');
        return;
      }

      const drawerHeight = dialog.getBoundingClientRect().height;
      const summaryHeight = summary.getBoundingClientRect().height;
      if (drawerHeight <= 0) return;

      const ratio = summaryHeight / drawerHeight;
      dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
    });
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
